import { CopyText } from "@/components/CopyText";
import { ItemCard, ItemCardDetails } from "@/components/ItemCard";
import { useActiveProject } from "@/features/project/ActiveProject";
import { currencyFormatter } from "@/utils/currencyFormatter";
import { type PrePlanningDetail, useInspirationDetails } from "@repo/api";
import {
  type ButtonType,
  LightMode,
  laptopPencilIcon,
  linkOutIcon,
} from "@repo/skapa";
import { getLanguageLocal, t } from "@repo/translation";
import { LocalizedDateAndTime, SimpleLabelField, useSettings } from "@repo/ui";
import { isDefined } from "@repo/utils";

export const useInspirationActions = (inspiration?: PrePlanningDetail) => {
  const { gpc_integration } = useSettings();
  const { opportunity } = useActiveProject();

  if (!inspiration) {
    return [];
  }

  const gpcEnabled = gpc_integration?.stores?.find(
    (unitId) => unitId === opportunity?.customerMeetingPointBuCode
  );
  const actions = [
    inspiration.imaginationPlanningUrl
      ? {
          id: `open-imagination__${inspiration.imaginationPlanningUrl}`,
          text: t("action.view_imagination"),
          title: t("action.view_imagination"),
          type: "emphasised" as ButtonType,
          modalType: "primary" as ButtonType,
          iconOnly: true,
          icon: linkOutIcon,
          onClick: () => {
            if (inspiration.imaginationPlanningUrl) {
              window.open(inspiration.imaginationPlanningUrl, "_blank");
            }
          },
        }
      : undefined,
    inspiration.gpcPlanUrl && gpcEnabled
      ? {
          id: `open-in-gpc__${inspiration.gpcPlanUrl}`,
          text: t("action.open_in_gpc"),
          title: t("action.open_in_gpc"),
          type: "emphasised" as ButtonType,
          modalType: "secondary" as ButtonType,
          icon: laptopPencilIcon,
          onClick: () => {
            window.open(inspiration.gpcPlanUrl, "_blank");
          },
        }
      : undefined,
  ].filter(isDefined);

  return actions;
};

export const InspirationItem = ({
  inspiration,
  setSelectedItem,
}: {
  inspiration: PrePlanningDetail;
  setSelectedItem: (item: PrePlanningDetail | undefined) => void;
}) => {
  const { opportunity } = useActiveProject();
  const { market, language, general } = useSettings();
  const { data } = useInspirationDetails(inspiration);
  const actions = useInspirationActions(inspiration);

  return (
    <ItemCard
      onClick={() => {
        if (data) {
          setSelectedItem(inspiration);
        }
      }}
      editable
      key={inspiration.sourceId}
      image={inspiration.teaserImageUrl ?? data?.image?.url}
      imageAlt={data?.image?.alt}
      amount={currencyFormatter({
        value: data?.price?.current,
        currency: data?.currency ?? opportunity?.currency,
        locale: getLanguageLocal(language, market),
        showSign: true,
        disableDecimals: Boolean(general?.localization?.disable_decimals),
        fractions: 2,
      })}
      details={
        <ItemCardDetails
          title={data?.title ?? t("label.inspiration")}
          description={
            <>
              <SimpleLabelField
                small
                label={t("label.added_at")}
                value={
                  <LocalizedDateAndTime
                    date={inspiration.sourceAddedAt ?? ""}
                  />
                }
                color={LightMode.colourTextAndIcon2}
              />
              {data?.createdAt && (
                <SimpleLabelField
                  small
                  label={t("label.created")}
                  value={<LocalizedDateAndTime date={data?.createdAt ?? ""} />}
                  color={LightMode.colourTextAndIcon2}
                />
              )}
              {data?.updatedAt && (
                <SimpleLabelField
                  small
                  label={t("label.updated")}
                  value={<LocalizedDateAndTime date={data?.updatedAt ?? ""} />}
                  color={LightMode.colourTextAndIcon2}
                />
              )}
            </>
          }
        />
      }
      actions={actions}
      addOn={<CopyText text={inspiration.planCode ?? inspiration.sourceId} />}
    />
  );
};
